import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('email'); // email -> verify
  const [role, setRole] = useState('employee'); // Store the role for resend
  const [resendCooldown, setResendCooldown] = useState(0);
  const { requestOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();

  const handleSendOtp = async (selectedRole) => {
    if (!email) {
      toast.error('Please enter email');
      return;
    }

    try {
      const res = await requestOtp(email, selectedRole);
      toast.success(res.message || 'OTP sent');
      if (res.code) {
        toast(`OTP (dev): ${res.code}`);
      }
      setRole(selectedRole);
      setStep('verify');
      setResendCooldown(30); // 30 second cooldown
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to send OTP');
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) {
      toast.error(`Please wait ${resendCooldown} seconds before resending`);
      return;
    }

    try {
      const res = await requestOtp(email, role);
      toast.success(res.message || 'OTP resent');
      if (res.code) {
        toast(`OTP (dev): ${res.code}`);
      }
      setResendCooldown(30); // Reset cooldown
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to resend OTP');
    }
  };

  // Handle cooldown timer
  useEffect(() => {
    let interval;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const handleVerify = async () => {
    if (!email || !otp) {
      toast.error('Please enter email and OTP');
      return;
    }

    try {
      const res = await verifyOtp(email, otp);
      toast.success('Login successful');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'OTP verification failed');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/30 to-background p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-primary text-primary-foreground font-bold text-2xl mb-4">
            {window.location.hostname.includes('charakpayments') || email.trim().toLowerCase().endsWith('@example.com') ? 'CH' : 'KT'}
          </div>
          <h1 className="text-3xl font-bold">
            {window.location.hostname.includes('charakpayments') || email.trim().toLowerCase().endsWith('@example.com') ? 'Charak Healthcare' : 'KarvaTech'}
          </h1>
          <p className="text-muted-foreground mt-2">Payment Tracking System</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Welcome Back</CardTitle>
            <CardDescription>Sign in to your account to continue</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="employee" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="employee">Employee</TabsTrigger>
                <TabsTrigger value="admin">Admin</TabsTrigger>
              </TabsList>

              <TabsContent value="employee">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSendOtp('employee');
                  }}
                  className="space-y-4"
                >
                  {step === 'email' ? (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="emp-email">Email</Label>
                        <Input
                          id="emp-email"
                          type="email"
                          placeholder="Enter your email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </div>
                      <Button type="submit" className="w-full">
                        Send OTP
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className="bg-muted p-3 rounded-lg text-center">
                        <p className="text-sm text-muted-foreground">OTP sent to:</p>
                        <p className="font-semibold text-sm">{email}</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="emp-otp">OTP</Label>
                        <Input
                          id="emp-otp"
                          type="text"
                          placeholder="Enter OTP"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value)}
                        />
                      </div>
                      <Button type="button" className="w-full" onClick={handleVerify}>
                        Verify OTP
                      </Button>
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={resendCooldown > 0}
                        className="w-full text-sm text-muted-foreground hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : 'Resend OTP'}
                      </button>
                    </>
                  )}
                </form>
              </TabsContent>

              <TabsContent value="admin">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSendOtp('admin');
                  }}
                  className="space-y-4"
                >
                  {step === 'email' ? (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="admin-email">Email</Label>
                        <Input
                          id="admin-email"
                          type="email"
                          placeholder="Enter your email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </div>
                      <Button type="submit" className="w-full">
                        Send OTP
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className="bg-muted p-3 rounded-lg text-center">
                        <p className="text-sm text-muted-foreground">OTP sent to:</p>
                        <p className="font-semibold text-sm">{email}</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="admin-otp">OTP</Label>
                        <Input
                          id="admin-otp"
                          type="text"
                          placeholder="Enter OTP"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value)}
                        />
                      </div>
                      <Button type="button" className="w-full" onClick={handleVerify}>
                        Verify OTP
                      </Button>
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={resendCooldown > 0}
                        className="w-full text-sm text-muted-foreground hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : 'Resend OTP'}
                      </button>
                    </>
                  )}
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};